export class MetricsTransformer {
  static transformHealthMetrics(resultsData, age, organization = {}) {
    const metrics = {};

    // Determine if we should use ATPL/HelloHealth icons
    const useAtplIcons = organization.flags?.isAtpl || organization.flags?.isHelloHealth;

    // Transform each health category
    if (resultsData.heartHealth) {
      metrics.heartHealth = this.transformHeartHealth(resultsData.heartHealth, useAtplIcons);
    }

    if (resultsData.bodyFunction) {
      metrics.bodyFunction = this.transformBodyFunction(resultsData.bodyFunction, useAtplIcons);
    }

    if (resultsData.physicalHealth) {
      metrics.physicalHealth = this.transformPhysicalHealth(resultsData.physicalHealth, age, useAtplIcons);
    }

    if (resultsData.skinHealth) {
      metrics.skinHealth = this.transformSkinHealth(resultsData.skinHealth, age, useAtplIcons);
    }

    if (resultsData.diseaseRisk) {
      metrics.diseaseRisk = this.transformDiseaseRisk(resultsData.diseaseRisk);
    }

    if (resultsData.metabolicHealth) {
      metrics.metabolicHealth = this.transformMetabolicHealth(resultsData.metabolicHealth);
    }

    return metrics;
  }

  static getSymbolPath(basePath, atplPath, useAtplIcons) {
    return useAtplIcons ? atplPath : basePath;
  }

  static transformHeartHealth(data, useAtplIcons = false) {
    return {
      name: 'Heart Health',
      value: data.score || 0,
      rating: this.getRating(data.score),
      resultHeading: 'Your Heart Health Metrics',
      results: [
        {
          name: 'Blood Pressure',
          value: data.bloodPressureSystolic || 0,
          diastolicbp: data.bloodPressureDiastolic || 0,
          symbol: this.getSymbolPath('/others/mmh.svg', '/others/atplResultsIcon/orange_bp.svg', useAtplIcons),
          label: 'mmHg',
          ratings: {
            low: [0, 90],
            optimal: [90, 120],
            normal: [120, 130],
            elevated: [130, 140],
            high: [140, 180],
          },
          diastolicbpRatings: {
            low: [0, 60],
            optimal: [60, 80],
            normal: [80, 84],
            elevated: [85, 89],
            high: [90, 120],
          },
          description:
            'Systolic blood pressure is the pressure in your arteries when your heart beats and pushes out blood. High values are bad, indicating potential hypertension; low values might be good but can be concerning if too low.',
          diastolicbpDescription:
            'Diastolic blood pressure is the pressure in your arteries when your heart is resting between beats. Low numbers could mean your heart is resting well between beats, which is good, but too low might indicate not enough pressure to push blood to all parts of your body, which is not good.',
        },
        {
          name: 'Irregular Heartbeat Count',
          value: data.irregularHeartBeatCount || 0,
          symbol: this.getSymbolPath(
            '/others/stethoscope.svg',
            '/others/atplResultsIcon/orange_stethoscope.svg',
            useAtplIcons,
          ),
          label: 'beats',
          ratings: {
            normal: [0, 0],
            elevated: [1, 4],
            high: [5, 10],
          },
          description:
            'Number of irregular heartbeats. If you have irregular heartbeats, it can mean your heart might not be pumping blood as smoothly as it should and may signal heart problems.',
        },
        {
          name: 'Heart Rate',
          value: data.pulseRate || 0,
          symbol: this.getSymbolPath(
            '/others/heart-sharp.svg',
            '/others/atplResultsIcon/orange_heart.svg',
            useAtplIcons,
          ),
          label: 'bpm',
          ratings: {
            low: [0, 40],
            optimal: [40, 60],
            normal: [60, 100],
            elevated: [100, 140],
          },
          description:
            'Pulse is the number of times your heart beats in one minute. This shows how hard your heart is working to pump blood around your body. Low values are usually good for resting adults, indicating a healthy heart, but too low might signal a problem whereas high values may indicate stress or heart issues.',
        },
      ],
    };
  }

  static transformBodyFunction(data, useAtplIcons = false) {
    return {
      name: 'Body Function',
      value: data.score || 0,
      rating: this.getRating(data.score),
      resultHeading: 'Your Body Function Metrics',
      results: [
        {
          name: 'Mental Stress Index',
          value: data.stressIndex || 0,
          symbol: this.getSymbolPath('/others/brain.svg', '/others/atplResultsIcon/orange_brain.svg', useAtplIcons),
          label: '',
          ratings: {
            optimal: [1, 2],
            normal: [2, 3],
            'high normal': [3, 4],
            elevated: [4, 5],
          },
          description:
            'Mental Stress Index assesses overall stress levels. High levels indicate excessive stress; low levels suggest good stress management or low stress.',
        },
        {
          name: 'Cardiac Workload',
          value: data.cardiacWorkload || 0,
          symbol: this.getSymbolPath(
            '/others/heart-pulse.svg',
            '/others/atplResultsIcon/orange_heart.svg',
            useAtplIcons,
          ),
          label: 'dB',
          ratings: {
            optimal: [0, 3.8],
            normal: [3.8, 3.9],
            'high normal': [3.9, 4.08],
            elevated: [4.08, 4.18],
            high: [4.18, 8.28],
          },
          description:
            'Cardiac Workload reflects how hard the heart works. High workload can stress the heart, while too low might show insufficient blood pumping.',
        },
        {
          name: 'Heart Rate Variability',
          value: data.heartRateVariability || 0,
          symbol: this.getSymbolPath(
            '/others/stethoscope.svg',
            '/others/atplResultsIcon/orange_stethoscope.svg',
            useAtplIcons,
          ),
          label: 'ms',
          ratings: {
            'very low': [0, 10.8],
            low: [10.8, 16.4],
            'low normal': [16.4, 35.5],
            normal: [35.5, 51.5],
            optimal: [51.5, 80],
          },
          description:
            'Heart rate variability is the variation in time between heartbeats. High variability is often a sign of a healthy heart, while low variability can indicate stress or cardiac health issues.',
        },
        {
          name: 'Vascular Capacity',
          value: data.vascularCapacity || 0,
          symbol: this.getSymbolPath('/others/droplet.svg', '/others/atplResultsIcon/orange_droplet.svg', useAtplIcons),
          label: 'sec',
          ratings: {
            'very low': [0, 0.79],
            low: [0.79, 1.12],
            'low normal': [1.12, 1.78],
            normal: [1.78, 2.11],
            optimal: [2.11, 15],
          },
          description:
            'Vascular Capacity indicates the health and function of blood vessels. High capacity suggests good blood flow and health; low capacity can signal cardiovascular issues.',
        },
      ],
    };
  }

  static transformPhysicalHealth(data, age, useAtplIcons = false) {
    const results = [
      {
        name: 'Body Mass Index',
        value: data.bodyMassIndex || 0,
        symbol: this.getSymbolPath(
          '/others/weight-hanging.svg',
          '/others/atplResultsIcon/orange-weight-hanging.svg',
          useAtplIcons,
        ),
        label: 'kg/m²',
        ratings: {
          scheme: 'bmi',
          underweight: [0, 18.5],
          normal: [18.5, 25],
          overweight: [25, 30],
          obese: [30, 60],
        },
        description:
          'BMI or Body Mass Index calculates body fat based on height and weight. High BMI indicates overweight/obesity; low BMI could mean underweight.',
      },
      {
        name: 'Body Shape Index',
        value: data.bodyShapeIndex || 0,
        symbol: this.getSymbolPath(
          '/others/weight-scale.svg',
          '/others/atplResultsIcon/orange-weight-scale.svg',
          useAtplIcons,
        ),
        label: '',
        ratings: {
          optimal: [0, 6.8],
          normal: [7.1, 7.6],
          'high normal': [7.6, 8.6],
          elevated: [8.6, 9.1],
          high: [9.1, 9.6],
        },
        description:
          'Body Shape Index (BSI) is a measure of body shape and health risks. Higher values can suggest a higher risk of heart disease; lower values indicate lower risk.',
      },
      {
        name: 'Breathing Rate',
        value: data.respiration || 0,
        symbol: this.getSymbolPath('/others/wind.svg', '/others/atplResultsIcon/orange_wind_.svg', useAtplIcons),
        label: 'brpm',
        ratings: {
          low: [0, 11],
          optimal: [12, 24],
          high: [25, 35],
        },
        description:
          'Breathing rate is the number of times you breathe in and out in a minute. This tells you if your lungs are providing enough oxygen to your body without too much effort. Both very high and very low rates can be bad, indicating potential breathing or health issues.',
      },
    ];

    // Add facial skin age if available
    if (data.facialSkinAge !== undefined && age) {
      results.splice(2, 0, {
        name: 'Facial Skin Age',
        value: data.facialSkinAge || age,
        symbol: this.getSymbolPath(
          '/others/womanface.svg',
          '/others/atplResultsIcon/orange_female-icon.png',
          useAtplIcons,
        ),
        label: 'years',
        ratings: {
          scheme: 'age',
          younger: [0, age],
          normal: [age, age + 1],
          older: [age + 1, 100],
        },
        description: 'AI-estimated facial skin age compared to chronological age using computer vision analysis.',
      });
    }

    return {
      name: 'Physical Health',
      value: data.score || 0,
      rating: this.getRating(data.score),
      resultHeading: 'Your Physical Health Metrics',
      results,
    };
  }

  static transformSkinHealth(data, age, useAtplIcons = false) {
    return {
      name: 'Skin Health',
      value: data.score || 0,
      rating: this.getRating(data.score),
      resultHeading: 'Your Skin Health Metrics',
      results: [
        {
          name: 'Facial Skin Age',
          value: data.facialSkinAge || age,
          symbol: this.getSymbolPath(
            '/others/womanface.svg',
            '/others/atplResultsIcon/orange_female-icon.png',
            useAtplIcons,
          ),
          label: 'years',
          ratings: {
            scheme: 'age',
            younger: [0, age],
            normal: [age, age + 1],
            older: [age + 1, 100],
          },
          description: 'AI-estimated facial skin age compared to chronological age using computer vision analysis.',
          confidence: data.facialSkinAgeConfidence || null,
        },
        {
          name: 'Skin Clarity',
          value: data.skinClarity || 0,
          symbol: this.getSymbolPath('/others/acne.png', '/others/atplResultsIcon/orange_acne.svg', useAtplIcons),
          label: '/100',
          ratings: {
            scheme: 'acne_health',
            poor: [0, 30], // Severe acne
            moderate: [30, 70], // Moderate acne
            good: [70, 100], // Clear/Low acne
          },
          description: 'AI-estimated skin clarity that indicates the level of acne concern.',
          confidence: data.skinClarityConfidence || 68,
        },
        {
          name: 'Skin Tone',
          value: data.skinTone || 0,
          symbol: this.getSymbolPath('/others/skin-tone.png', '/others/atplResultsIcon/orange_tone.svg', useAtplIcons),
          label: '/100',
          ratings: {
            scheme: 'tone_quality',
            poor: [0, 30],
            fair: [30, 50],
            good: [50, 70],
            very_good: [70, 85],
            excellent: [85, 100],
          },
          description: 'Skin tone uniformity analyzed according to the Fitzpatrick scale.',
          confidence: data.skinToneConfidence || 80,
        },
        {
          name: 'Skin Balance',
          value: data.skinBalance || 0,
          symbol: this.getSymbolPath('/others/droplet.svg', '/others/atplResultsIcon/orange_droplet.svg', useAtplIcons),
          label: '/100',
          ratings: {
            scheme: 'skin_balance',
            needs_care: [0, 70], // Dry/Oily (needs attention)
            balanced: [70, 95], // Good balance
            optimal: [95, 100], // Normal (ideal)
          },
          description:
            'Skin balance measure indicating the oil-moisture ratio. Normal skin indicates optimal oil-moisture balance.',
          confidence: data.skinBalanceConfidence || 87,
        },
      ],
    };
  }

  static transformDiseaseRisk(data) {
    return {
      name: 'Overall Disease Risk',
      value: data.score || 0,
      rating: this.getRiskRating(data.score),
      resultHeading: 'Your Disease Risk Factors',
      results: [
        {
          name: 'Cardiovascular Risk',
          value: data.cardiovascularRisk || 0,
          symbol: null,
          percentSign: '%',
          ratings: {
            scheme: 'risk',
            'very low': [0, 5],
            low: [5, 7.25],
            moderate: [7.25, 10],
            high: [10, 20],
            'very high': [20, 100],
          },
          description:
            'Cardiovascular Disease Risk is the likelihood of developing heart-related conditions. Higher risk requires lifestyle changes; lower risk suggests good heart health.',
        },
        {
          name: 'Hypertriglyceridemia Risk',
          value: data.hypertriglyceridemiaRisk || 0,
          symbol: null,
          percentSign: '%',
          ratings: {
            scheme: 'risk',
            'very low': [0, 25],
            low: [25, 45],
            moderate: [45, 55],
            high: [55, 77.5],
            'very high': [77.5, 100],
          },
          description:
            'Hypertriglyceridemia Risk is the chance of elevated triglycerides in blood in the next 10 years. High risk indicates potential for heart issues; lower risk is better.',
        },
        {
          name: 'Heart Disease Risk',
          value: data.heartDiseaseRisk || 0,
          symbol: null,
          percentSign: '%',
          ratings: {
            scheme: 'risk',
            'very low': [0, 5],
            low: [5, 7.25],
            moderate: [7.25, 10],
            high: [10, 20],
            'very high': [20, 100],
          },
          description:
            'Heart Disease Risk indicates the probability of developing conditions affecting the heart. Higher risk may require medical intervention and lifestyle changes; lower risk reflects better heart health.',
        },
        {
          name: 'Stroke Risk',
          value: data.strokeRisk || 0,
          symbol: null,
          percentSign: '%',
          ratings: {
            scheme: 'risk',
            'very low': [0, 3.3],
            low: [3.3, 4.79],
            moderate: [4.79, 6.6],
            high: [6.6, 13.2],
            'very high': [13.2, 100],
          },
          description:
            'Stroke Risk is the probability of suffering a stroke in the next 10 years. Higher risk highlights the need for intervention; lower risk indicates less cause for concern.',
        },
      ],
    };
  }

  static transformMetabolicHealth(data) {
    return {
      name: 'Metabolic Health',
      value: data.diabetesRisk || 0,
      rating: this.getRiskRating(data.diabetesRisk),
      resultHeading: 'Your Metabolic Health Metrics',
      results: [
        {
          name: 'Risk of HbA1c >5.7%',
          value: data.hba1cRisk || 0,
          symbol: null,
          percentSign: '%',
          ratings: {
            scheme: 'risk',
            'very low': [0, 25],
            low: [25, 45],
            moderate: [45, 55],
            high: [55, 77.5],
            'very high': [77.5, 100],
          },
          description:
            'Hemoglobin A1C Risk is the risk of higher than normal A1C levels, indicating diabetes risk. High levels necessitate action; lower levels are preferred.',
        },
        {
          name: 'Fasting Blood Glucose >5.5mmol/L',
          value: data.bloodGlucose || 0,
          symbol: null,
          percentSign: '%',
          ratings: {
            scheme: 'risk',
            'very low': [0, 25],
            low: [25, 45],
            moderate: [45, 55],
            high: [55, 77.5],
            'very high': [77.5, 100],
          },
          description:
            'Fasting Blood Glucose Risk is the chance of having high fasting blood sugar. High levels indicate diabetes risk; lower levels are normal.',
        },
        {
          name: 'Type 2 Diabetes Risk',
          value: data.diabetesRisk || 0,
          symbol: null,
          percentSign: '%',
          ratings: {
            scheme: 'risk',
            'very low': [0, 25],
            low: [25, 45],
            moderate: [45, 55],
            high: [55, 77.5],
            'very high': [77.5, 100],
          },
          description:
            'Type 2 Diabetes Risk is the likelihood of developing type 2 diabetes in the next 10 years. High risk necessitates preventive actions; low risk means less worry.',
        },
      ],
    };
  }

  static getRating(score) {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Poor';
  }

  static getRiskRating(score) {
    if (score <= 20) return 'Low';
    if (score <= 40) return 'Moderate';
    if (score <= 60) return 'High';
    return 'Very High';
  }
}
